import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, ArrowUpRight, Mail, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

const emailSchema = z.object({ email: z.string().email("Inserisci un'email valida") });

export default function Login() {
    const { user, requestOtp, verifyOtp } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [step, setStep] = useState("email"); // email | otp
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [error, setError] = useState(null);
    const [info, setInfo] = useState(null);
    const [devOtp, setDevOtp] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const { register, handleSubmit, formState: { errors } } = useForm({
        resolver: zodResolver(emailSchema),
    });

    useEffect(() => {
        if (user) navigate(user.role === "admin" ? "/admin" : "/app", { replace: true });
    }, [user, navigate]);

    useEffect(() => {
        const magicErr = searchParams.get("magic_error");
        if (magicErr) {
            const messages = {
                session_expired: "La tua sessione è scaduta. Inserisci la tua email per ricevere un nuovo codice.",
            };
            setError(messages[magicErr] || decodeURIComponent(magicErr));
        }
    }, [searchParams]);

    const onSubmitEmail = async ({ email: e }) => {
        setError(null); setInfo(null); setDevOtp(null); setSubmitting(true);
        try {
            const res = await requestOtp(e);
            setEmail(e);
            if (res.status === "EMAIL_NOT_FOUND") {
                setInfo("La tua email non risulta nei nostri registri. Compila il modulo assistito per ricevere supporto dal team.");
            } else {
                setStep("otp");
                if (res.dev_otp) setDevOtp(res.dev_otp);
            }
        } catch (err) {
            setError(err?.response?.data?.detail || "Errore durante l'invio del codice");
        } finally { setSubmitting(false); }
    };

    const onSubmitOtp = async () => {
        setError(null); setSubmitting(true);
        try {
            const res = await verifyOtp(email, otp);
            const target = res.role === "admin" ? "/admin" : (location.state?.from?.pathname || "/app");
            navigate(target, { replace: true });
        } catch (err) {
            setError(err?.response?.data?.detail || "Codice non valido");
        } finally { setSubmitting(false); }
    };

    return (
        <div className="min-h-screen bg-[#050505] text-white flex" data-testid="login-page">
            {/* LEFT — visual */}
            <div className="hidden lg:flex w-1/2 relative border-r border-zinc-900 p-12 flex-col justify-between bg-[#080808] overflow-hidden">
                <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-acid opacity-10 blur-3xl" />
                <div className="absolute bottom-0 -right-40 w-[500px] h-[500px] rounded-full bg-magenta opacity-10 blur-3xl" />
                <Link to="/" className="relative flex items-center gap-3" data-testid="login-logo">
                    <div className="h-7 w-7 bg-acid" />
                    <span className="font-display text-xl">StartupItalia<span className="text-acid">.</span></span>
                </Link>
                <div className="relative">
                    <span className="label-tag">— Investor Platform</span>
                    <h1 className="font-display text-6xl xl:text-7xl mt-4 tracking-tighter leading-[0.9]">
                        Bentornato<br />
                        nel tuo <span className="font-serif-italic text-acid">capitale.</span>
                    </h1>
                    <p className="text-zinc-400 mt-8 max-w-md">
                        Accesso senza password: ti inviamo un codice monouso via email, valido 10 minuti.
                    </p>
                </div>
                <div className="relative text-zinc-600 text-xs font-grotesk uppercase tracking-widest">
                    Powered by StartupItalia · v1.0
                </div>
            </div>

            {/* RIGHT — form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12">
                <div className="w-full max-w-md">
                    <Link to="/" className="inline-flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-widest font-grotesk hover:text-acid transition" data-testid="login-back-link">
                        <ArrowLeft className="h-4 w-4" /> Torna al sito
                    </Link>

                    {step === "email" && (
                        <form onSubmit={handleSubmit(onSubmitEmail)} className="mt-12 space-y-8" data-testid="login-email-form">
                            <div>
                                <span className="label-tag">Step 1 di 2</span>
                                <h2 className="font-display text-5xl mt-3 tracking-tighter">
                                    Accedi alla tua<br />area riservata.
                                </h2>
                            </div>
                            <div>
                                <label className="label-tag block mb-3" htmlFor="email">Indirizzo email</label>
                                <div className="relative">
                                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-600" />
                                    <input
                                        id="email"
                                        type="email"
                                        autoComplete="email"
                                        placeholder="nome.cognome@example.it"
                                        data-testid="login-email-input"
                                        className="w-full bg-[#0F0F0F] border border-zinc-800 text-white pl-12 pr-4 py-4 font-grotesk focus:outline-none focus:border-acid transition"
                                        {...register("email")}
                                    />
                                </div>
                                {errors.email && <p className="text-magenta text-sm mt-2 font-grotesk" data-testid="email-error">{errors.email.message}</p>}
                            </div>
                            {error && <div className="border border-magenta/40 bg-magenta/10 text-magenta px-4 py-3 text-sm font-grotesk" data-testid="login-error">{error}</div>}
                            {info && (
                                <div className="border border-acid/40 bg-acid/5 text-zinc-300 px-4 py-4 text-sm font-grotesk" data-testid="login-info">
                                    {info}<br />
                                    <Link to="/non-riesco-accedere" className="text-acid font-bold inline-flex items-center gap-1 mt-2">
                                        Apri il modulo assistito <ArrowUpRight className="h-3 w-3" />
                                    </Link>
                                </div>
                            )}
                            <button type="submit" className="btn-rebel w-full justify-center" disabled={submitting} data-testid="login-request-otp-button">
                                {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Invio…</> : <>Invia codice di accesso <ArrowUpRight className="h-4 w-4" /></>}
                            </button>
                            <p className="text-center text-xs text-zinc-500 font-grotesk uppercase tracking-widest">
                                <Link to="/non-riesco-accedere" className="hover:text-acid" data-testid="login-cant-access">Non riesco ad accedere</Link>
                            </p>
                        </form>
                    )}

                    {step === "otp" && (
                        <div className="mt-12 space-y-8" data-testid="login-otp-form">
                            <div>
                                <span className="label-tag">Step 2 di 2</span>
                                <h2 className="font-display text-5xl mt-3 tracking-tighter">
                                    Inserisci il<br />tuo <span className="font-serif-italic text-acid">codice.</span>
                                </h2>
                                <p className="text-zinc-400 mt-4 text-sm">
                                    Abbiamo inviato un codice a 6 cifre a <span className="text-white font-grotesk">{email}</span>. Il codice è valido per 10 minuti.
                                </p>
                            </div>

                            {devOtp && (
                                <div className="border border-acid/40 bg-acid/5 px-4 py-3 text-sm font-grotesk" data-testid="dev-otp-banner">
                                    <span className="label-tag text-acid">DEV MODE</span>
                                    <div className="mt-1 text-zinc-300">Codice generato: <span className="text-acid font-bold tracking-widest">{devOtp}</span></div>
                                </div>
                            )}

                            <div className="flex justify-center" data-testid="otp-input-wrapper">
                                <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="login-otp-input">
                                    <InputOTPGroup>
                                        <InputOTPSlot index={0} data-testid="otp-slot-0" />
                                        <InputOTPSlot index={1} data-testid="otp-slot-1" />
                                        <InputOTPSlot index={2} data-testid="otp-slot-2" />
                                        <InputOTPSlot index={3} data-testid="otp-slot-3" />
                                        <InputOTPSlot index={4} data-testid="otp-slot-4" />
                                        <InputOTPSlot index={5} data-testid="otp-slot-5" />
                                    </InputOTPGroup>
                                </InputOTP>
                            </div>

                            {error && <div className="border border-magenta/40 bg-magenta/10 text-magenta px-4 py-3 text-sm font-grotesk text-center" data-testid="otp-error">{error}</div>}

                            <div className="flex flex-col gap-3">
                                <button onClick={onSubmitOtp} disabled={otp.length !== 6 || submitting} className="btn-rebel w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed" data-testid="login-verify-otp-button" type="button">
                                    {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Verifico…</> : <>Verifica e accedi <ArrowUpRight className="h-4 w-4" /></>}
                                </button>
                                <button onClick={() => { setStep("email"); setOtp(""); setError(null); }} className="text-zinc-500 hover:text-white text-xs uppercase tracking-widest font-grotesk" data-testid="otp-change-email">
                                    Cambia indirizzo email
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
